/*
 * The ERP in the app's top bar, without loading its screens: Odoo's apps from the gateway, each an
 * entry under /erp, and the app in use (per tab), which the ERP's screens set and the bar marks.
 * Until the person has signed in to the ERP there is one entry for it, which leads to its sign-in.
 */
import { useSyncExternalStore } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { NavEntry } from '@/app/shell/TopNav'
import { odoo } from './odoo/api'
import type { MenuNode } from './odoo/api'

/** Where the ERP's screens live in the app. */
export const ERP_BASE = '/erp'

export const ODOO_MENUS = ['odoo-menus']

const KEY = 'derp.odoo.app'
let current: number | null = (() => {
    try { return Number(sessionStorage.getItem(KEY)) || null } catch { return null }
})()
const listeners = new Set<() => void>()

export function setCurrentApp(id: number | null) {
    if (id === current) return
    current = id
    try { if (id) sessionStorage.setItem(KEY, String(id)) } catch { /* per-tab convenience only */ }
    listeners.forEach((l) => l())
}

export function useCurrentApp(): number | null {
    return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l) }, () => current)
}

export function useOdooMenus(enabled = true) {
    return useQuery({ queryKey: ODOO_MENUS, queryFn: odoo.menus, staleTime: 10 * 60_000, enabled })
}

/** Odoo's Settings app keeps its English name in Estonian: it gets ours ("Seaded"). */
const SETTINGS = 'base.menu_administration'

/**
 * Every app as a top-bar entry (`to` inside the ERP: /app/<id>); the one in use is current (not on
 * the overview, which belongs to no app).
 */
export function appEntries(apps: MenuNode[], currentId: number | null, inApp: boolean, settingsLabel: string): NavEntry[] {
    return apps.map((a) => ({
        key: `app-${a.id}`,
        label: a.xmlid === SETTINGS ? settingsLabel : a.name,
        to: `/app/${a.id}`,
        current: inApp && a.id === currentId,
    }))
}

/**
 * The ERP's entries in the app's top bar, at their addresses in the app (under /erp): Odoo's apps
 * once the person is signed in to the ERP, else one entry for the ERP itself (`labels.erp`).
 * `pathname` is the app's (an ERP screen's is under /erp).
 */
export function useErpEntries(enabled: boolean, pathname: string, labels: { erp: string; settings: string }): NavEntry[] {
    const menus = useOdooMenus(enabled)
    const currentId = useCurrentApp()
    if (!enabled) return []
    const inErp = pathname === ERP_BASE || pathname.startsWith(`${ERP_BASE}/`)
    if (!menus.data) return [{ key: 'erp', label: labels.erp, to: ERP_BASE, current: inErp }]
    const inApp = inErp && pathname !== ERP_BASE && pathname !== `${ERP_BASE}/`
    return appEntries(menus.data.apps, currentId, inApp, labels.settings).map((entry) => ({ ...entry, to: `${ERP_BASE}${entry.to}` }))
}
